import type { TFunction } from 'i18next';

/**
 * Metric definition with label and optional description.
 * getLabel and getDescription use literal translation keys so the pre-commit
 * hook can detect them.
 */
export type EmailPulseMetricDefinition = {
  /** Returns translated label (visible to pre-commit hook scanner) */
  getLabel: (t: TFunction) => string;
  /** Returns translated description, or empty string if none exists (visible to pre-commit hook scanner) */
  getDescription: (t: TFunction) => string;
  /** Optional: Whether this metric represents a "bad" value (inverts colors in UI) */
  invertColor?: boolean;
};

/**
 * Central registry of all Email Pulse metrics.
 * This is the single source of truth for metric labels and descriptions.
 *
 * IMPORTANT: Use literal translation keys in getLabel/getDescription so the
 * pre-commit hook can detect them. Do NOT use variables or string concatenation.
 */
export const EMAIL_PULSE_DEFINITIONS: Record<
  string,
  EmailPulseMetricDefinition
> = {
  // Main metrics with descriptions
  totalEmailsOpened: {
    getLabel: t => t('page.emailPulse.totalEmailsOpened.label'),
    getDescription: t => t('page.emailPulse.totalEmailsOpened.description'),
  },
  emailsSent: {
    getLabel: t => t('page.emailPulse.emailsSent.label'),
    getDescription: t => t('page.emailPulse.emailsSent.description'),
  },
  emailsDelivered: {
    getLabel: t => t('page.emailPulse.emailsDelivered.label'),
    getDescription: t => t('page.emailPulse.emailsDelivered.description'),
  },
  deliveryRate: {
    getLabel: t => t('page.emailPulse.deliveryRate.label'),
    getDescription: t => t('page.emailPulse.deliveryRate.description'),
  },
  openRate: {
    getLabel: t => t('page.emailPulse.openRate.label'),
    getDescription: t => t('page.emailPulse.openRate.description'),
  },
  clickRate: {
    getLabel: t => t('page.emailPulse.clickRate.label'),
    getDescription: t => t('page.emailPulse.clickRate.description'),
  },
  bounceRate: {
    getLabel: t => t('page.emailPulse.table.bounceRate.label'),
    getDescription: t => t('page.emailPulse.table.bounceRate.description'),
  },
  unsubscribeRate: {
    getLabel: t => t('page.emailPulse.unsubscribeRate.label'),
    getDescription: t => t('page.emailPulse.unsubscribeRate.description'),
    invertColor: true,
  },
  spamReportRate: {
    getLabel: t => t('page.emailPulse.spamReportRate.label'),
    getDescription: t => t('page.emailPulse.spamReportRate.description'),
    invertColor: true,
  },
  emailsBounced: {
    getLabel: t => t('page.emailPulse.emailsBounced.label'),
    getDescription: t => t('page.emailPulse.emailsBounced.description'),
  },
  emailsDropped: {
    getLabel: t => t('page.emailPulse.emailsDropped.label'),
    getDescription: t => t('page.emailPulse.emailsDropped.description'),
  },
  emailsOpened: {
    getLabel: t => t('page.emailPulse.emailsOpened.label'),
    getDescription: t => t('page.emailPulse.emailsOpened.description'),
  },
  uniqueOpens: {
    getLabel: t => t('page.emailPulse.table.uniqueOpens.label'),
    getDescription: t => t('page.emailPulse.uniqueOpens.description'),
  },
  clicks: {
    getLabel: t => t('page.emailPulse.table.clicks.label'),
    getDescription: t => t('page.emailPulse.clicks.description'),
  },
  uniqueClicks: {
    getLabel: t => t('page.emailPulse.table.uniqueClicks.label'),
    getDescription: t => t('page.emailPulse.uniqueClicks.description'),
  },
  clickThroughRate: {
    getLabel: t => t('page.emailPulse.table.clickThroughRate.label'),
    getDescription: t => t('page.emailPulse.clickThroughRate.description'),
  },
  unsubscribes: {
    getLabel: t => t('page.emailPulse.table.unsubscribes.label'),
    getDescription: t => t('page.emailPulse.unsubscribes.description'),
  },
  spamReports: {
    getLabel: t => t('page.emailPulse.table.spamReports.label'),
    getDescription: t => t('page.emailPulse.spamReports.description'),
  },
  emailsDeferred: {
    getLabel: t => t('page.emailPulse.emailsDeferred.label'),
    getDescription: t => t('page.emailPulse.emailsDeferred.description'),
  },
  uniqueEmailsDeferred: {
    getLabel: t => t('page.emailPulse.uniqueEmailsDeferred.label'),
    getDescription: t => t('page.emailPulse.uniqueEmailsDeferred.description'),
  },

  // Chart and summary card only metrics
  mobileOpens: {
    getLabel: t => t('page.emailPulse.mobileOpens.label'),
    getDescription: t => t('page.emailPulse.mobileOpens.description'),
  },
  desktopOpens: {
    getLabel: t => t('page.emailPulse.desktopOpens.label'),
    getDescription: t => t('page.emailPulse.desktopOpens.description'),
  },
  notReportedOpens: {
    getLabel: t => t('page.emailPulse.notReportedOpens.label'),
    getDescription: t => t('page.emailPulse.notReportedOpens.description'),
  },
  machineOpens: {
    getLabel: t => t('page.emailPulse.machineOpens.label'),
    getDescription: t => t('page.emailPulse.machineOpens.description'),
  },

  // Bounce breakdown metrics
  hardBounces: {
    getLabel: t => t('page.emailPulse.hardBounces.label'),
    getDescription: t => t('page.emailPulse.hardBounces.description'),
  },
  softBounces: {
    getLabel: t => t('page.emailPulse.softBounces.label'),
    getDescription: t => t('page.emailPulse.softBounces.description'),
  },

  // Identifier columns
  messageTitle: {
    getLabel: t => t('page.emailPulse.table.messageTitle.label'),
    getDescription: () => '', // No description exists yet
  },
  offerTitle: {
    getLabel: t => t('page.emailPulse.table.offerTitle.label'),
    getDescription: () => '', // No description exists yet
  },
  emailTypeDisplay: {
    getLabel: t => t('page.emailPulse.table.emailType.label'),
    getDescription: () => '', // No description exists yet
  },
  communicationTitle: {
    getLabel: t => t('page.emailPulse.table.communicationTitle.label'),
    getDescription: t =>
      t('page.emailPulse.table.communicationTitle.description'),
  },

  // Legacy aliases (for backward compatibility with API definitions)
  spamFlagRate: {
    getLabel: t => t('page.emailPulse.spamReportRate.label'),
    getDescription: t => t('page.emailPulse.spamReportRate.description'),
    invertColor: true,
  },
};
